import { useState } from "react";
import { Link } from "react-router-dom";
import { Icon, type IconName } from "./icons";
import { EmptyState, Skeleton, formatDateTime, useApi } from "./ui";
import { HomePage } from "./site/Home";
import { PlayerHead, RankBadge, formatRankingValue, sourceUnavailable, type RankingKind } from "./site/minecraft";
import { Standings } from "./site/Standings";
import { areaVisible, useArea, useSite } from "./site/context";
// Every public page shares the site frame (header, account menu, footer).
import { AreaGate, SiteShell as MinecraftShell } from "./site/SiteShell";

export { MinecraftShell, RankBadge };

export function MinecraftServerV2() {
  return <AreaGate id="public.home"><HomePage/></AreaGate>;
}

/* ---------- Minecraft leaderboards ---------- */

const rankingOptions: Record<RankingKind, { label: string; endpoint: string; title: string; detail: string; icon: IconName; valueLabel: string }> = {
  islands: { label: "Island top", endpoint: "/api/v1/public/minecraft/islands", title: "Island top", detail: "Islands ranked by island level.", icon: "island", valueLabel: "Level" },
  balance: { label: "Balance", endpoint: "/api/v1/public/minecraft/economy", title: "Balance top", detail: "Players ranked by the money they hold.", icon: "coin", valueLabel: "Balance" },
  playtime: { label: "Play time", endpoint: "/api/v1/public/minecraft/rankings/playtime", title: "Play time", detail: "Players ranked by total time on the server.", icon: "clock", valueLabel: "Time" },
  "mob-kills": { label: "Mob kills", endpoint: "/api/v1/public/minecraft/rankings/mob-kills", title: "Mobs killed", detail: "Players ranked by hostile mobs defeated.", icon: "sword", valueLabel: "Kills" },
};

export function MinecraftIslandTopV2() {
  return <AreaGate id="public.leaderboards"><ServerLeaderboards/></AreaGate>;
}

function ServerLeaderboards() {
  const [kind, setKind] = useState<RankingKind>("islands");
  const option = rankingOptions[kind];
  const state = useApi<any>(option.endpoint);
  const data = state.data?.data;
  const items: any[] = Array.isArray(data?.items) ? data.items : [];
  const islands = kind === "islands";
  const { me } = useSite();
  const community = areaVisible(useArea("public.community"), Boolean(me.user));
  return <MinecraftShell><main id="main">
    <div className="container page-top"><header className="page-head">
      <h1>Server leaderboards</h1>
      <p>Live standings from the Minecraft server. Pick a board to see who is on top right now.</p>
    </header></div>
    <section className="band" aria-labelledby="board-title">
      <div className="container">
        <div className="lb-picker segmented" role="group" aria-label="Leaderboard category">
          {(Object.entries(rankingOptions) as [RankingKind, typeof option][]).map(([value, item]) => <button type="button" aria-pressed={kind === value} onClick={() => setKind(value)} key={value}><Icon name={item.icon} size={18}/><span>{item.label}</span></button>)}
        </div>
        <div className="lb-title">
          <div><h2 id="board-title">{option.title}</h2><p>{option.detail}</p></div>
          {data?.calculatedAt && <p className="lb-updated">Updated {formatDateTime(data.calculatedAt)}{state.data?.status === "cached" ? " (saved copy)" : ""}</p>}
        </div>
        <div aria-busy={state.loading} aria-live="polite">
          {state.loading && !data ? <Skeleton rows={5} label="Loading the leaderboard"/>
            : state.error || !data ? <EmptyState icon="alert" title="This leaderboard is not available right now" description="We could not reach the Minecraft server for these standings. Try again in a few minutes." action={<div className="empty-actions"><button type="button" className="button" onClick={state.reload}><Icon name="refresh" size={16}/>Try again</button></div>}/>
            : items.length === 0 ? <EmptyState icon={option.icon} title="Nobody is ranked yet" description={sourceUnavailable(data) ? "The plugin that tracks this board is not running on the server right now." : "Nobody has qualified for this board yet. Play a little and you could be first."}/>
            : <Standings label={`${option.title} standings`} column={islands ? "Island" : "Player"} valueLabel={option.valueLabel} noun={islands ? "island" : "player"}
              items={items.map((item) => {
                const head = islands ? item.owner?.name : item.name;
                return { key: `${kind}-${item.id || item.uuid || item.rank}`, rank: item.rank, name: item.name, detail: islands && item.owner?.name ? `Owned by ${item.owner.name}` : null, picture: <PlayerHead name={head} size={36}/>, leaderPicture: <PlayerHead name={head} size={72}/>, value: formatRankingValue(kind, item, data) };
              })}/>}
        </div>
      </div>
    </section>
    {community && <section className="band raised" aria-labelledby="more-boards">
      <div className="container lb-more">
        <div><h2 id="more-boards">The Discord keeps score too</h2><p>The Discord has its own leaderboards for chat, voice, invites, and events.</p></div>
        <Link className="button" to="/discord/leaderboard"><Icon name="arrowRight" size={16}/>Discord leaderboards</Link>
      </div>
    </section>}
  </main></MinecraftShell>;
}
